import { type DisplayRowId } from './common'

// 表示中の回答行だけに絞る共通処理。
//
// WHY: 顧客データ画面はユーザーが回答項目を個別に非表示にでき、表テキストは
// buildNineSegmentsTableInfo / buildFiveSegmentsTableInfo が hiddenRowIds を適用した
// 結果をシリアライズする。一方 Top5 ブロック (appendDriverTop5Block /
// appendSegmentTop5Block) はフックが返す生の answersArrayData を見ていたため、
// 非表示にした項目が表テキストから消えてもブロックには残っていた。プロンプトは
// 「ブロックのまま使う。表から選び直さない」契約なので、LLM は表に存在しない項目を
// そのまま書き写してしまう。
//
// 表とブロックで可視判定がずれないよう、両者がこの1関数を共有する。

// 構造的部分型: 9segs/5segs いずれの回答行 (および同形のモック) も受けられる。
type AnswerRow = {
  question: { question_id: string }
  answerOption: { option: number }
}

/**
 * 回答行を表の行ID (`${question_id}-${option}`) に変換します。
 *
 * @param row - 回答行。
 * @returns hiddenRowIds と突き合わせる行ID。
 */
export const toDisplayRowId = (row: AnswerRow): DisplayRowId =>
  `${row.question.question_id}-${row.answerOption.option}`

/**
 * 設問ごとの回答行から、非表示にされた行を取り除きます。
 *
 * 全行が非表示になった設問は、設問ごと落とします (表側の挙動と同じ)。
 *
 * @param answersArrayData - 設問ごとの回答行。
 * @param hiddenRowIds - 非表示にされた行ID。
 * @returns 表示中の行だけを残した設問ごとの回答行。
 */
export const filterVisibleAnswersArrayData = <T extends AnswerRow>(
  answersArrayData: readonly (readonly T[])[],
  hiddenRowIds: readonly DisplayRowId[],
): T[][] =>
  answersArrayData
    .map((answersData) =>
      answersData.filter(
        (answerData) => !hiddenRowIds.includes(toDisplayRowId(answerData)),
      ),
    )
    .filter((answersData) => 0 < answersData.length)

// 顧客属性行の可視判定。表側 (buildNineSegmentsTableInfo / buildFiveSegmentsTableInfo) と
// Top5 ブロックが同じ判定を共有するためにここに置く。回答行と違い、行IDは
// customerAttribute.id そのもの。
type CustomerAttributeRow = {
  customerAttribute: { id: string }
}

/**
 * 属性グループごとの顧客属性行から、非表示にされた行を取り除きます。
 *
 * 全行が非表示になったグループは、グループごと落とします (表側の挙動と同じ)。
 *
 * @param customerAttributesArrayData - 属性グループごとの顧客属性行。
 * @param hiddenRowIds - 非表示にされた行ID。
 * @returns 表示中の行だけを残した属性グループごとの顧客属性行。
 */
export const filterVisibleCustomerAttributesArrayData = <
  T extends CustomerAttributeRow,
>(
  customerAttributesArrayData: readonly (readonly T[])[],
  hiddenRowIds: readonly DisplayRowId[],
): T[][] =>
  customerAttributesArrayData
    .map((customerAttributesData) =>
      customerAttributesData.filter(
        (customerAttributeData) =>
          !hiddenRowIds.includes(customerAttributeData.customerAttribute.id),
      ),
    )
    .filter((customerAttributesData) => 0 < customerAttributesData.length)
